/**
 * Shared Node Styling Utilities for Visual Editors
 * Centralized styling logic for VisualEditor and VisualStoryPlanner
 */

/**
 * Lighten a hex color by a percentage
 * @param {string} color - Hex color (e.g., "#667eea")
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} - Lightened hex color
 */
export const lightenColor = (color, percent) => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten by moving toward white (255, 255, 255)
  const amount = percent / 100;
  const newR = Math.min(255, Math.round(r + (255 - r) * amount));
  const newG = Math.min(255, Math.round(g + (255 - g) * amount));
  const newB = Math.min(255, Math.round(b + (255 - b) * amount));
  
  // Convert back to hex
  const toHex = (n) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

/**
 * Get node styling based on node type and user preferences
 * @param {Object} node - Node object with properties
 * @param {Object} styleConfig - User style configuration
 * @returns {Object} - Style object for the node
 */
export const getNodeStyle = (node, styleConfig = {}) => {
  const isStart = node.id === 'Start' || node.name === 'Start';
  const isChapter = node.chapterId && node.chapterId !== 'chapter-1';
  const isDirectUrl = node.isDirectUrl === true;
  const isH1 = node.nodeLevel === 'h1';
  const isH2 = node.nodeLevel === 'h2';
  
  // Default colors
  const defaultBg = styleConfig.nodeBg || '#667eea';
  const defaultFontColor = styleConfig.fontColor || '#ffffff';
  const startBg = styleConfig.startBg || '#10b981';
  const chapterBg = styleConfig.chapterBg || '#f59e0b';
  const borderColor = styleConfig.borderColor || '#4f46e5';
  
  let style = {
    backgroundColor: defaultBg,
    color: defaultFontColor,
    border: `2px solid ${borderColor}`,
    borderRadius: '8px',
    padding: '12px 16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  };
  
  // Start node styling
  if (isStart) {
    style.backgroundColor = startBg;
    style.border = `2px solid ${lightenColor(startBg, -20)}`;
  }
  
  // Chapter node styling
  else if (isChapter) {
    style.backgroundColor = chapterBg;
    style.border = `2px solid ${lightenColor(chapterBg, -20)}`;
  }
  
  // Direct URL nodes (no bounding box)
  else if (isDirectUrl) {
    style.backgroundColor = 'transparent';
    style.border = 'none';
    style.boxShadow = 'none';
    style.color = styleConfig.urlLinkColor || '#3b82f6';
    style.fontWeight = '500';
    style.textDecoration = 'underline';
    style.cursor = 'pointer';
  }
  
  // H1 nodes (30% lighter)
  else if (isH1) {
    style.backgroundColor = lightenColor(defaultBg, 30);
    style.color = '#1a202c'; // Dark font
    style.border = `2px solid ${lightenColor(borderColor, 20)}`;
  }
  
  // H2 nodes (30% lighter)
  else if (isH2) {
    style.backgroundColor = lightenColor(defaultBg, 30);
    style.color = '#1a202c'; // Dark font
    style.border = `2px solid ${lightenColor(borderColor, 20)}`;
  }
  
  return style;
};

/**
 * Get connection line style based on user preferences
 * @param {Object} styleConfig - User style configuration
 * @returns {Object} - Style object for connection lines
 */
export const getConnectionStyle = (styleConfig = {}) => {
  return {
    stroke: styleConfig.lineColor || '#94a3b8',
    strokeWidth: styleConfig.lineWidth || 2
  };
};

/**
 * Default style configuration
 */
export const defaultStyleConfig = {
  nodeBg: '#667eea',
  fontColor: '#ffffff',
  startBg: '#10b981',
  chapterBg: '#f59e0b',
  borderColor: '#4f46e5',
  lineColor: '#94a3b8',
  lineWidth: 2,
  urlLinkColor: '#3b82f6'
};

/**
 * Load style config from localStorage
 */
export const loadStyleConfig = () => {
  try {
    const saved = localStorage.getItem('visual_editor_style_config');
    if (saved) {
      return { ...defaultStyleConfig, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error('Error loading style config:', e);
  }
  return defaultStyleConfig;
};

/**
 * Save style config to localStorage
 */
export const saveStyleConfig = (config) => {
  try {
    localStorage.setItem('visual_editor_style_config', JSON.stringify(config));
  } catch (e) {
    console.error('Error saving style config:', e);
  }
};
